/**
 * OwnerStatisticsPage Component
 * 오너 통계 대시보드 UI
 */
import React from 'react';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ClockIcon,
  ArrowTrendingUpIcon as TrendingUpIcon,
  ArrowTrendingDownIcon as TrendingDownIcon,
  ArrowPathIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useOwnerStatistics } from '../use-cases/useOwnerStatistics';
import { ownerStatisticsService } from '../services/OwnerStatisticsService';
import type { StatisticsPeriod } from '../models/OwnerStatistics';

const OwnerStatisticsPage: React.FC = () => {
  const {
    loading,
    error,
    statistics,
    selectedPeriod,
    lastUpdated,
    timeSlotChartData,
    planChartData,
    handlePeriodChange,
    handleRefresh,
    fetchStatistics,
  } = useOwnerStatistics();

  // 성장률 아이콘 렌더링
  const renderGrowthIcon = (rate: number) => {
    const iconType = ownerStatisticsService.getGrowthIconType(rate);
    if (iconType === 'up') return <TrendingUpIcon className="h-4 w-4 text-green-500" />;
    if (iconType === 'down') return <TrendingDownIcon className="h-4 w-4 text-red-500" />;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">❌ {error}</p>
            <button
              onClick={fetchStatistics}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-500">통계 데이터가 없습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">통계 리포트</h1>
              <p className="text-gray-600 mt-2">독서실 운영 통계를 확인하고 분석하세요</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={selectedPeriod}
                onChange={(e) => handlePeriodChange(Number(e.target.value) as StatisticsPeriod)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={7}>최근 7일</option>
                <option value={30}>최근 30일</option>
                <option value={90}>최근 90일</option>
              </select>
              <button
                onClick={handleRefresh}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span>새로고침</span>
              </button>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            <CalendarIcon className="inline h-4 w-4 mr-1" />
            마지막 업데이트: {lastUpdated.toLocaleString('ko-KR')}
          </div>
        </div>

        {/* 요약 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* 일일 매출 */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">일일 매출</p>
                <p className="text-2xl font-bold text-gray-900">
                  {ownerStatisticsService.formatCurrency(statistics.revenue.daily_revenue)}
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              {renderGrowthIcon(statistics.revenue.growth_rate_daily)}
              <span
                className={`text-sm ml-1 ${ownerStatisticsService.getGrowthColorClass(statistics.revenue.growth_rate_daily)}`}
              >
                {ownerStatisticsService.formatPercent(statistics.revenue.growth_rate_daily)} (전일
                대비)
              </span>
            </div>
          </div>

          {/* 좌석 점유율 */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">좌석 점유율</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statistics.seats.occupancy_rate}%
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <BuildingOfficeIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-600">
                {statistics.seats.occupied_seats}/{statistics.seats.total_seats} 좌석 사용 중
              </span>
            </div>
          </div>

          {/* 활성 회원 */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">활성 회원</p>
                <p className="text-2xl font-bold text-gray-900">
                  {ownerStatisticsService.formatNumber(statistics.members.active_members)}
                </p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <UsersIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-600">
                전체 {ownerStatisticsService.formatNumber(statistics.members.total_members)}명 중
              </span>
            </div>
          </div>

          {/* 평균 이용시간 */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">평균 이용시간</p>
                <p className="text-2xl font-bold text-gray-900">
                  {ownerStatisticsService.formatSessionDuration(
                    statistics.seats.average_session_duration
                  )}
                </p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-600">
                일일 {ownerStatisticsService.formatNumber(statistics.seats.daily_sessions)}회 이용
              </span>
            </div>
          </div>
        </div>

        {/* 차트 섹션 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* 시간대별 이용 현황 */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">시간대별 이용 현황</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timeSlotChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="이용건수" fill={ownerStatisticsService.CHART_COLORS[0]} />
                <Bar dataKey="매출" fill={ownerStatisticsService.CHART_COLORS[1]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 요금제별 이용 분포 */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">요금제별 이용 분포</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={planChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry: any) => `${entry.name} (${entry.percentage}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {planChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        ownerStatisticsService.CHART_COLORS[
                          index % ownerStatisticsService.CHART_COLORS.length
                        ]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 상세 매출 정보 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-600 mb-2">주간 매출</h4>
            <p className="text-2xl font-bold text-gray-900">
              {ownerStatisticsService.formatCurrency(statistics.revenue.weekly_revenue)}
            </p>
            <div className="mt-2 flex items-center">
              {renderGrowthIcon(statistics.revenue.growth_rate_weekly)}
              <span
                className={`text-sm ml-1 ${ownerStatisticsService.getGrowthColorClass(statistics.revenue.growth_rate_weekly)}`}
              >
                {ownerStatisticsService.formatPercent(statistics.revenue.growth_rate_weekly)}
              </span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-600 mb-2">월간 매출</h4>
            <p className="text-2xl font-bold text-gray-900">
              {ownerStatisticsService.formatCurrency(statistics.revenue.monthly_revenue)}
            </p>
            <div className="mt-2 flex items-center">
              {renderGrowthIcon(statistics.revenue.growth_rate_monthly)}
              <span
                className={`text-sm ml-1 ${ownerStatisticsService.getGrowthColorClass(statistics.revenue.growth_rate_monthly)}`}
              >
                {ownerStatisticsService.formatPercent(statistics.revenue.growth_rate_monthly)}
              </span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-600 mb-2">총 매출</h4>
            <p className="text-2xl font-bold text-gray-900">
              {ownerStatisticsService.formatCurrency(statistics.revenue.total_revenue)}
            </p>
            <div className="mt-2">
              <span className="text-sm text-gray-600">
                {ownerStatisticsService.getPeriodLabel(selectedPeriod)} 기준
              </span>
            </div>
          </div>
        </div>

        {/* 회원 상세 정보 */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">회원 현황</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">오늘 신규 회원</p>
              <p className="text-xl font-bold text-gray-900">
                {ownerStatisticsService.formatNumber(statistics.members.new_members_today)}명
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">주간 신규 회원</p>
              <p className="text-xl font-bold text-gray-900">
                {ownerStatisticsService.formatNumber(statistics.members.new_members_week)}명
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">월간 신규 회원</p>
              <p className="text-xl font-bold text-gray-900">
                {ownerStatisticsService.formatNumber(statistics.members.new_members_month)}명
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">재방문율</p>
              <p className="text-xl font-bold text-gray-900">
                {statistics.members.retention_rate}%
              </p>
            </div>
          </div>
        </div>

        {/* 피크 타임 정보 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-800 mb-2">피크 타임 안내</h4>
          <p className="text-blue-700">
            가장 혼잡한 시간대:{' '}
            {ownerStatisticsService.formatPeakHours(statistics.seats.peak_hours)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OwnerStatisticsPage;
